import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";

// #270 (UC-2): Write-Mutations des Git-Content-Managers — jede Änderung ist ein Commit.
// Nach Erfolg werden tree+file-Queries des Repos invalidiert (frischer sha!).

interface SaveFileInput {
  repoId: string;
  path: string;
  content: string;
  message: string;
  sha?: string;
  // #278: Ziel-Entwurf. Bei governed Repos PFLICHT — ohne ihn 403.
  ref?: string;
}

interface UploadFileInput {
  repoId: string;
  path: string;
  contentBase64: string;
  message: string;
  sha?: string;
  // #278: Ziel-Entwurf. Bei governed Repos PFLICHT — ohne ihn 403.
  ref?: string;
}

interface DeleteFileInput {
  repoId: string;
  path: string;
  message: string;
  sha: string;
  // #278: Ziel-Entwurf. Bei governed Repos PFLICHT — ohne ihn 403.
  ref?: string;
}

function useInvalidateRepo() {
  const qc = useQueryClient();
  return (repoId: string) => {
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "tree", repoId] });
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "file", repoId] });
    // #278: ein Commit auf einem Entwurf ändert dessen Änderungs-Stand — sonst zeigte die
    // Vorschau nach dem Speichern noch die alte Datei-Liste.
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "drafts", repoId] });
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "changes", repoId] });
  };
}

export function useSaveRepoFile(): UseMutationResult<{ sha: string }, Error, SaveFileInput> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateRepo();
  return useMutation({
    mutationFn: (input) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.put<{ sha: string }>(`/git-content/repos/${input.repoId}/file`, {
        path: input.path,
        content: input.content,
        message: input.message,
        sha: input.sha,
        ref: input.ref,
      });
    },
    onSuccess: (_d, input) => invalidate(input.repoId),
  });
}

export function useUploadRepoFile(): UseMutationResult<{ sha: string }, Error, UploadFileInput> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateRepo();
  return useMutation({
    mutationFn: (input) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.post<{ sha: string }>(`/git-content/repos/${input.repoId}/upload`, {
        path: input.path,
        contentBase64: input.contentBase64,
        message: input.message,
        sha: input.sha,
        ref: input.ref,
      });
    },
    onSuccess: (_d, input) => invalidate(input.repoId),
  });
}

export function useDeleteRepoFile(): UseMutationResult<{ deleted: boolean }, Error, DeleteFileInput> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateRepo();
  return useMutation({
    mutationFn: (input) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.delete<{ deleted: boolean }>(`/git-content/repos/${input.repoId}/file`, {
        path: input.path,
        message: input.message,
        sha: input.sha,
        ref: input.ref,
      });
    },
    onSuccess: (_d, input) => invalidate(input.repoId),
  });
}

// #277 (UC-4): Ordner-Upload — viele Dateien, EIN Commit.
//
// Die UI schickt die Dateien einzeln (der Browser liefert bei einer Ordner-Auswahl genau
// das); das ZIP-Format bleibt dem Server-Endpunkt für hochgeladene Archive vorbehalten.
export interface BulkUploadInput {
  repoId: string;
  targetPath: string;
  message: string;
  /** PFLICHT — Entwurfs-Branch. Ohne ihn lehnt der Server ab (kein Live-Schreiben). */
  ref: string;
  files: Array<{ path: string; contentBase64: string }>;
}

export interface BulkUploadResult {
  commitSha: string;
  ref: string;
  files: Array<{ path: string; operation: "create" | "update" }>;
}

export function useBulkUpload(): UseMutationResult<BulkUploadResult, Error, BulkUploadInput> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateRepo();
  return useMutation({
    mutationFn: ({ repoId, ...body }: BulkUploadInput) => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.post<BulkUploadResult>(`/git-content/repos/${encodeURIComponent(repoId)}/bulk-upload`, body);
    },
    onSuccess: (_r, v) => invalidate(v.repoId),
  });
}

// #290 (UC-5): Repo archivieren (Wegroll-Rotation serverseitig, nie Hard-Delete).
// Für tpl- über die Fach-Route (409 TEMPLATE_IN_USE bei referenzierendem Profil),
// für plg- über die Werkbank-Route — beide landen in derselben Service-Methode.
export function useDeleteWorkspaceTemplate(): UseMutationResult<{ archivedAs: string }, Error, string> {
  const { session, logout } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.delete<{ archivedAs: string }>(`/workspace-templates/${encodeURIComponent(id)}`);
    },
    onSuccess: () => {
      // #290 (Review-Fund): der GANZE git-content-Baum (Repo-Inventar + Diagnose), nicht
      // nur tree/file EINES Repos — sonst steht die archivierte Vorlage weiter in der
      // Sidebar (Klick → 404) und die frische Leiche fehlt unter „Nicht zugeordnet".
      void qc.invalidateQueries({ queryKey: ["resources", "git-content"] });
      void qc.invalidateQueries({ queryKey: ["resources", "workspace-templates"] });
    },
  });
}

export function useArchiveRepo(): UseMutationResult<{ archivedAs: string }, Error, string> {
  const { session, logout } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (repoId: string) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.delete<{ archivedAs: string }>(`/git-content/repos/${encodeURIComponent(repoId)}`);
    },
    // s.o. — Inventar + Diagnose müssen das Archiv sofort widerspiegeln.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["resources", "git-content"] }),
  });
}

// #290 (UC-6): Leiche archivieren (Name wird serverseitig positiv verifiziert).
export function useArchiveUnassignedRepo(): UseMutationResult<{ archivedAs: string }, Error, string> {
  const { session, logout } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.delete<{ archivedAs: string }>(`/git-content/unassigned/${encodeURIComponent(name)}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["resources", "git-content"] }),
  });
}
